/**
 * @file     LocalBlogCard.tsx
 * @purpose  Article summary on the reading template: cover image, category
 *           kicker, title, excerpt and date with read time, as one link with no
 *           box chrome. `featured` lays the same content out wide beside a
 *           larger image for the first post on the index.
 */
'use client';

import Image from 'next/image';
import Link from 'next/link';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@/hooks/useAnalytics';
import { focusRingSx, headingFont, hubColors, hubRadius, reducedMotionSx } from '@/components/hub/hubKitStyles';
import type { LocalBlogPost } from '../../types/LocalBlogPost';
import { fadeInOnLoad, photoZoom } from '@/core/motion';

type LocalBlogCardProps = {
  post: LocalBlogPost;
  locationTag?: string;
  /** Set true for above-the-fold cards to eager-load the cover image */
  priority?: boolean;
  /** Wide layout with an H2 title, for the lead post on the index. */
  featured?: boolean;
};

const CATEGORY_I18N: Record<LocalBlogPost['category'], { key: string; defaultValue: string }> = {
  'market-update': { key: 'blog.card.category.marketUpdate', defaultValue: 'Market Update' },
  'buyer-guide': { key: 'blog.card.category.buyerGuide', defaultValue: 'Buyer Guide' },
  'seller-guide': { key: 'blog.card.category.sellerGuide', defaultValue: 'Seller Guide' },
  presale: { key: 'blog.card.category.presale', defaultValue: 'Presale' },
  neighbourhood: { key: 'blog.card.category.neighbourhood', defaultValue: 'Neighbourhood' },
};

const formatDate = (iso: string, locale: string) =>
  new Date(iso).toLocaleDateString(locale || 'en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });

export const LocalBlogCard = ({
  post,
  locationTag = 'blog_card',
  priority: isPriority = false,
  featured = false,
}: LocalBlogCardProps) => {
  const { trackCtaClick } = useAnalytics();
  const { t, i18n } = useTranslation();

  const detailPath = `/blog/${post.slug}`;
  const category = CATEGORY_I18N[post.category];
  const meta = [
    formatDate(post.publishedAt, i18n.language),
    t('blog.card.minRead', { defaultValue: '{{count}} min read', count: post.readTimeMinutes }),
  ].join(' · ');

  return (
    <Box
      component={Link}
      href={detailPath}
      onClick={() => trackCtaClick('Read Article', locationTag, detailPath)}
      sx={{
        display: 'grid',
        gridTemplateColumns: featured ? { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' } : 'minmax(0, 1fr)',
        alignItems: featured ? 'center' : 'start',
        columnGap: featured ? { md: 5 } : 0,
        rowGap: featured ? 2.5 : 1.75,
        color: hubColors.textPrimary,
        textDecoration: 'none',
        borderRadius: `${hubRadius.panel}px`,
        ...focusRingSx,
        '&:focus-visible': { ...focusRingSx['&:focus-visible'], outlineOffset: 6 },
        '&:hover .blog-card-title': { textDecoration: 'underline', textDecorationThickness: '1px', textUnderlineOffset: '4px' },
        '@media (hover: hover)': { '&:hover .blog-card-image img': { transform: photoZoom.transform } },
      }}
    >
      <Box
        className="blog-card-image"
        sx={{
          position: 'relative',
          // Covers are 16:9 editorial plates with text near the edges; never crop them.
          aspectRatio: '16 / 9',
          borderRadius: `${hubRadius.panel}px`,
          overflow: 'hidden',
          backgroundColor: hubColors.skeleton,
          '& img': { transition: photoZoom.transition, ...reducedMotionSx },
        }}
      >
        {post.coverImage ? (
          <Image
            src={post.coverImage}
            alt=""
            fill
            quality={85}
            sizes={featured ? '(max-width: 899px) 100vw, 600px' : '(max-width: 600px) 100vw, (max-width: 1200px) 50vw, 380px'}
            style={{ objectFit: 'cover' }}
            ref={isPriority ? undefined : fadeInOnLoad}
            priority={isPriority}
            loading={isPriority ? 'eager' : 'lazy'}
          />
        ) : null}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
        <Box component="span" sx={{ fontSize: 13, fontWeight: 500, color: hubColors.accentText }}>
          {t(category.key, { defaultValue: category.defaultValue })}
        </Box>
        <Box
          component={featured ? 'h2' : 'h3'}
          className="blog-card-title"
          sx={{
            m: 0,
            fontFamily: headingFont,
            fontSize: featured ? { xs: 24, md: 34 } : 18,
            fontWeight: 600,
            lineHeight: featured ? 1.15 : 1.3,
            letterSpacing: featured ? '-0.02em' : '-0.01em',
            textWrap: 'balance',
          }}
        >
          {post.title}
        </Box>
        <Box
          component="span"
          sx={{
            fontSize: featured ? 15.5 : 14.5,
            lineHeight: featured ? 1.65 : 1.5,
            color: hubColors.textSecondary,
            display: '-webkit-box',
            WebkitLineClamp: featured ? 4 : 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {post.excerpt}
        </Box>
        <Box component="span" sx={{ fontSize: 13, color: hubColors.textMuted }}>
          {meta}
        </Box>
      </Box>
    </Box>
  );
};
